import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import { Center, Loader } from "@mantine/core";

import { ProtectedRoute } from "./auth/ProtectedRoute";
import { PublicRoute } from "./auth/PublicRoute";
import { RoleGate } from "./auth/RoleGate";
import { AdminShell } from "./components/layout/AdminShell";

import { LoginPage } from "./pages/LoginPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { DashboardPage } from "./pages/DashboardPage";
import { DriversPage } from "./pages/drivers/DriversPage";
import { DriverDetailPage } from "./pages/drivers/DriverDetailPage";
import { TripsPage } from "./pages/trips/TripsPage";
import { TripDetailPage } from "./pages/trips/TripDetailPage";
import { UsersPage } from "./pages/users/UsersPage";
import { UserDetailPage } from "./pages/users/UserDetailPage";
import { RideTypesPage } from "./pages/rideTypes/RideTypesPage";
import { TollLocationsPage } from "./pages/tollLocations/TollLocationsPage";
import { TagsPage } from "./pages/tags/TagsPage";
import { CouponsPage } from "./pages/coupons/CouponsPage";
import { TransactionsPage } from "./pages/transactions/TransactionsPage";
import { ReportsPage } from "./pages/reports/ReportsPage";
import { SettlementsPage } from "./pages/settlements/SettlementsPage";
import { PayoutsPage } from "./pages/payouts/PayoutsPage";
import { PayoutCataloguePage } from "./pages/payouts/PayoutCataloguePage";
import { TaxReceiptsPage } from "./pages/taxReceipts/TaxReceiptsPage";
import { LatraMonthlyReportPage } from "./pages/latra/LatraMonthlyReportPage";
import { SettingsPage } from "./pages/settings/SettingsPage";
import { SosPage } from "./pages/sos/SosPage";
import { QueriesPage } from "./pages/support/QueriesPage";
import { LostItemsPage } from "./pages/support/LostItemsPage";
import { AdminsPage } from "./pages/admins/AdminsPage";
import { HomePage } from "./pages/home/HomePage";
import { InboxPage } from "./pages/inbox/InboxPage";
import { ChangePasswordPage } from "./pages/account/ChangePasswordPage";
import { NotFoundPage } from "./pages/NotFoundPage";

// Heavy console screens (maps, charts) load on first visit, not up front.
const LivePage = lazy(() => import("./pages/live/LivePage").then((m) => ({ default: m.LivePage })));
const FinancePage = lazy(() => import("./pages/finance/FinancePage").then((m) => ({ default: m.FinancePage })));
const AnalyticsPage = lazy(() => import("./pages/analytics/AnalyticsPage").then((m) => ({ default: m.AnalyticsPage })));
const MonthlySummaryPage = lazy(() => import("./pages/reports/MonthlySummaryPage").then((m) => ({ default: m.MonthlySummaryPage })));
const ReferralsPage = lazy(() => import("./pages/referrals/ReferralsPage").then((m) => ({ default: m.ReferralsPage })));
const ReferralDetailPage = lazy(() => import("./pages/referrals/ReferralDetailPage").then((m) => ({ default: m.ReferralDetailPage })));
const AgentDetailPage = lazy(() => import("./pages/referrals/AgentDetailPage").then((m) => ({ default: m.AgentDetailPage })));
const AuditLogPage = lazy(() => import("./pages/audit/AuditLogPage").then((m) => ({ default: m.AuditLogPage })));

export default function App() {
  return (
    <Suspense
      fallback={
        <Center h="60vh">
          <Loader />
        </Center>
      }
    >
    <Routes>
      {/* Public */}
      <Route element={<PublicRoute />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>
      {/* Password reset — reachable whether or not signed in */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Authenticated app shell */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AdminShell />}>
          <Route path="/" element={<HomePage />} />
          <Route
            path="/overview"
            element={
              <RoleGate module="dashboard">
                <DashboardPage />
              </RoleGate>
            }
          />
          <Route
            path="/live"
            element={
              <RoleGate module="live">
                <LivePage />
              </RoleGate>
            }
          />
          <Route
            path="/inbox"
            element={
              <RoleGate module="inbox">
                <InboxPage />
              </RoleGate>
            }
          />
          <Route
            path="/finance"
            element={
              <RoleGate module="finance">
                <FinancePage />
              </RoleGate>
            }
          />
          <Route
            path="/analytics"
            element={
              <RoleGate module="analytics">
                <AnalyticsPage />
              </RoleGate>
            }
          />
          <Route
            path="/reports/monthly"
            element={
              <RoleGate module="monthly">
                <MonthlySummaryPage />
              </RoleGate>
            }
          />


          <Route
            path="/drivers"
            element={
              <RoleGate module="drivers">
                <DriversPage />
              </RoleGate>
            }
          />
          <Route
            path="/drivers/:id"
            element={
              <RoleGate module="drivers">
                <DriverDetailPage />
              </RoleGate>
            }
          />

          <Route
            path="/referrals"
            element={
              <RoleGate module="referrals">
                <ReferralsPage />
              </RoleGate>
            }
          />
          <Route
            path="/referrals/agents/:id"
            element={
              <RoleGate module="referrals">
                <AgentDetailPage />
              </RoleGate>
            }
          />
          <Route
            path="/referrals/:id"
            element={
              <RoleGate module="referrals">
                <ReferralDetailPage />
              </RoleGate>
            }
          />

          <Route
            path="/trips"
            element={
              <RoleGate module="trips">
                <TripsPage />
              </RoleGate>
            }
          />
          <Route
            path="/trips/:id"
            element={
              <RoleGate module="trips">
                <TripDetailPage />
              </RoleGate>
            }
          />

          <Route
            path="/users"
            element={
              <RoleGate module="users">
                <UsersPage />
              </RoleGate>
            }
          />
          <Route
            path="/users/:id"
            element={
              <RoleGate module="users">
                <UserDetailPage />
              </RoleGate>
            }
          />

          <Route
            path="/ride-types"
            element={
              <RoleGate module="ride-types">
                <RideTypesPage />
              </RoleGate>
            }
          />

          <Route
            path="/tags"
            element={
              <RoleGate module="tags">
                <TagsPage />
              </RoleGate>
            }
          />
          <Route
            path="/toll-locations"
            element={
              <RoleGate module="toll-locations">
                <TollLocationsPage />
              </RoleGate>
            }
          />

          <Route
            path="/coupons"
            element={
              <RoleGate module="coupons">
                <CouponsPage />
              </RoleGate>
            }
          />
          <Route
            path="/transactions"
            element={
              <RoleGate module="transactions">
                <TransactionsPage />
              </RoleGate>
            }
          />
          <Route
            path="/settlements"
            element={
              <RoleGate module="settlements">
                <SettlementsPage />
              </RoleGate>
            }
          />
          <Route
            path="/payouts"
            element={
              <RoleGate module="payouts">
                <PayoutsPage />
              </RoleGate>
            }
          />
          <Route
            path="/payout-destinations"
            element={
              <RoleGate module="payout-catalogue">
                <PayoutCataloguePage />
              </RoleGate>
            }
          />
          <Route
            path="/tax-receipts"
            element={
              <RoleGate module="tax-receipts">
                <TaxReceiptsPage />
              </RoleGate>
            }
          />
          <Route
            path="/latra"
            element={
              <RoleGate module="latra">
                <LatraMonthlyReportPage />
              </RoleGate>
            }
          />
          <Route
            path="/reports"
            element={
              <RoleGate module="reports">
                <ReportsPage />
              </RoleGate>
            }
          />
          <Route
            path="/settings"
            element={
              <RoleGate module="settings">
                <SettingsPage />
              </RoleGate>
            }
          />

          <Route
            path="/sos"
            element={
              <RoleGate module="sos">
                <SosPage />
              </RoleGate>
            }
          />
          <Route
            path="/queries"
            element={
              <RoleGate module="queries">
                <QueriesPage />
              </RoleGate>
            }
          />
          <Route
            path="/lost-items"
            element={
              <RoleGate module="lost-items">
                <LostItemsPage />
              </RoleGate>
            }
          />

          <Route
            path="/admins"
            element={
              <RoleGate module="admins">
                <AdminsPage />
              </RoleGate>
            }
          />

          <Route
            path="/audit"
            element={
              <RoleGate module="audit">
                <AuditLogPage />
              </RoleGate>
            }
          />

          <Route path="/account/password" element={<ChangePasswordPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
    </Suspense>
  );
}
